import React, { useState, useEffect, useMemo } from 'react';
import { Link } from "react-router-dom";
import axios from 'axios';
import SEO from '../../../components/common/SEO';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function ListingYatras() {
  const [yatras, setYatras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const pageSize = 20;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  useEffect(() => {
    const fetchYatras = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/yatras/');
        setYatras(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch yatras. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchYatras();
  }, []);

  const sortedData = useMemo(() => {
    let filteredData = [...yatras].filter(row =>
      (row.yatra_name && row.yatra_name.toLowerCase().includes(search.toLowerCase())) ||
      (row.city && row.city.toLowerCase().includes(search.toLowerCase()))
    );

    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        const valA = a[sortConfig.key] || '';
        const valB = b[sortConfig.key] || '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filteredData;
  }, [yatras, search, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const handleDelete = async (yatraId) => {
    try {
      await apiClient.delete(`/yatras/${yatraId}`);
      setYatras(currentYatras => currentYatras.filter(p => p.id !== yatraId));
      setSuccessMessage('Yatra deleted successfully!');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      setError(null);
    } catch (err) {
      setError('Failed to delete yatra. Please try again.');
      console.error(err);
    }
  };
  const confirmDelete = async () => {
    if (deleteId) {
      await handleDelete(deleteId);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };
  const renderStatusBadge = (status, reason) => {
    let badgeClass = 'bg-secondary';
    if (status === 'Published') badgeClass = 'bg-success';
    else if (status === 'Under Review') badgeClass = 'bg-warning';
    else if (status === 'Rejected') badgeClass = 'bg-danger';

    return (
      <div>
        <span className={`badge ${badgeClass}`}>{status || 'Published'}</span>
        {status === 'Rejected' && reason && (
          <div className="text-danger mt-1" style={{ fontSize: '11px', whiteSpace: 'normal', maxWidth: '150px' }}>
            <i className="ri-error-warning-line align-middle me-1"></i>
            {reason}
          </div>
        )}
      </div>
    );
  };
  return (
    <div className="row">
      <SEO title="Yatras Management | Mangoloops" />
      <div className="col-12">
        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
          <h4 className="mb-sm-0">My Yatras</h4>
        </div>
      </div>
      <div className="col-lg-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="card-title mb-0">My Yatras</h4>
            <div className="d-flex flex-wrap gap-2">
              <input type="text" className="form-control" placeholder="Search..." value={search} onChange={handleSearchChange} style={{ width: '250px' }} />
              <Link to="/admin/add-yatra" className="btn btn-soft-secondary">Add Yatra</Link>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center p-4"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
              <>
                {error && <div className="alert alert-danger">{error}</div>}
                {successMessage && <div className="alert alert-success">{successMessage}</div>}

                <div className="table-responsive">
                  <table className="table table-bordered table-striped align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "80px" }}>Sr. No.</th>
                        <th style={{ width: "100px" }}>Image</th>
                        <th onClick={() => requestSort('yatra_name')} style={{ cursor: 'pointer' }}>Yatra Name{renderSortArrow('yatra_name')}</th>
                        <th onClick={() => requestSort('city')} style={{ cursor: 'pointer' }}>City{renderSortArrow('city')}</th>
                        <th onClick={() => requestSort('yatra_price')} style={{ cursor: 'pointer' }}>Price{renderSortArrow('yatra_price')}</th>
                        <th onClick={() => requestSort('offer_price')} style={{ cursor: 'pointer' }}>Offer Price{renderSortArrow('offer_price')}</th>
                        <th onClick={() => requestSort('status')} style={{ cursor: 'pointer' }}>Status{renderSortArrow('status')}</th>
                        <th onClick={() => requestSort('created_at')} style={{ cursor: 'pointer', width: "150px" }}>Created Date{renderSortArrow('created_at')}</th>
                        <th style={{ width: "100px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.length > 0 ? paginatedData.map((row, index) => (
                        <tr key={row.id}>
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>
                            {row.Images && row.Images.length > 0 ? (
                              <img
                                src={row.Images[0].image_url}
                                alt={row.yatra_name}
                                style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                              />
                            ) : (
                              <span className="text-muted">No Image</span>
                            )}
                          </td>
                          <td>
                            <Link to={`/admin/edit-yatra/${row.id}`}>{row.yatra_name}</Link>
                          </td>
                          <td>{row.city}</td>
                          <td>₹{row.yatra_price}</td>
                          <td>₹{row.offer_price}</td>
                          <td>
                            {row.status ? renderStatusBadge(row.status, row.rejection_reason) : 'N/A'}
                          </td>
                          <td>
                            {row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className='text-center'>
                            <button className="btn btn-sm btn-soft-danger" title="Delete" onClick={() => { setDeleteId(row.id); setShowDeleteModal(true); }}>
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan="9" className="text-center">No matching records found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {totalPages > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                    <span className="text-muted">
                      Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} entries
                    </span>
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
                        </li>
                        {[...Array(totalPages).keys()].map(num => (
                          <li key={num + 1} className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(num + 1)}>{num + 1}</button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
                        </li>
                      </ul>
                      {showDeleteModal && (
                        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                          <div className="modal-dialog">
                            <div className="modal-content">
                              <div className="modal-header">
                                <h5 className="modal-title">Confirm Deletion</h5>
                                <button type="button" className="btn-close" onClick={cancelDelete}></button>
                              </div>
                              <div className="modal-body">
                                <p>You are about to permanently delete this listing.</p>
                                <p>Once deleted, it cannot be recovered.</p>
                                <p className="mb-0">Do you want to continue?</p>
                              </div>
                              <div className="modal-footer">
                                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Yes, Delete</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ListingYatras;
